import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Lock, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Token mancante nella URL");
      return;
    }
    if (pwd.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password aggiornata. Ora puoi accedere.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16 bg-bg">
      <form
        onSubmit={submit}
        data-testid="reset-password-form"
        className="w-full max-w-md bg-white border border-line rounded-lg shadow-card p-8"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
          <KeyRound className="w-3 h-3" /> Nuova password
        </div>
        <h1 className="mt-4 font-display text-3xl text-ink">Reimposta la password</h1>
        <p className="mt-2 text-sm text-ink-muted">
          Scegli una nuova password sicura (minimo 6 caratteri).
        </p>

        <label className="block mt-6">
          <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Nuova password</span>
          <div className="relative">
            <Lock className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type={show ? "text" : "password"}
              required
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="reset-pwd-input"
              className="w-full h-11 pl-10 pr-10 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
            />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </label>
        <label className="block mt-4">
          <span className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">Ripeti password</span>
          <div className="relative">
            <Lock className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type={show ? "text" : "password"}
              required
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              data-testid="reset-pwd2-input"
              className="w-full h-11 pl-10 pr-3 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
            />
          </div>
        </label>

        <button
          type="submit"
          disabled={loading || !token}
          data-testid="reset-submit"
          className="mt-6 w-full h-11 bg-brand text-white font-display uppercase tracking-widest text-sm rounded-md hover:bg-brand-dark hover:shadow-brand-glow disabled:opacity-60"
        >
          {loading ? "Aggiornamento…" : "Imposta nuova password"}
        </button>

        {!token && (
          <p className="mt-4 text-xs text-red-600 text-center">
            Link non valido. Torna alla pagina di{" "}
            <Link to="/password-dimenticata" className="underline">recupero password</Link>.
          </p>
        )}
      </form>
    </div>
  );
}
